// src/services/storageService.js - FIXED WITH AUTO INIT
import { STORAGE_KEYS, APPLICATION_STATUS } from '../constants/statusConstants';

class StorageService {
  constructor() {
    // Auto-initialize dummy data when service is created
    this.initializeDummyData();
  }

  // Generic CRUD operations
  getAll(key) {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  }

  getById(key, id) {
    const items = this.getAll(key);
    return items.find(item => item.id === id) || null;
  }

  save(key, data) {
    localStorage.setItem(key, JSON.stringify(data));
  }

  create(key, item) {
    const items = this.getAll(key);
    const newItem = { ...item, id: Date.now().toString() };
    items.push(newItem);
    this.save(key, items);
    return newItem;
  }

  update(key, id, updates) {
    const items = this.getAll(key);
    const index = items.findIndex(item => item.id === id);
    if (index !== -1) {
      items[index] = { ...items[index], ...updates };
      this.save(key, items);
      return items[index];
    }
    return null;
  }

  delete(key, id) {
    const items = this.getAll(key).filter(item => item.id !== id);
    this.save(key, items);
  }

  // Company methods
  getCompanies() {
    return this.getAll(STORAGE_KEYS.COMPANIES);
  }

  getCompany(id) {
    return this.getById(STORAGE_KEYS.COMPANIES, id);
  }

  createCompany(company) {
    return this.create(STORAGE_KEYS.COMPANIES, company);
  }

  updateCompany(id, updates) {
    return this.update(STORAGE_KEYS.COMPANIES, id, updates);
  }

  deleteCompany(id) {
    this.delete(STORAGE_KEYS.COMPANIES, id);
  }

  // Job methods
  getJobs() {
    return this.getAll(STORAGE_KEYS.JOBS);
  }

  getJob(id) {
    return this.getById(STORAGE_KEYS.JOBS, id);
  }

  getJobsByCompany(companyId) {
    const jobs = this.getAll(STORAGE_KEYS.JOBS);
    return jobs.filter(job => job.companyId === companyId);
  }

  createJob(job) {
    return this.create(STORAGE_KEYS.JOBS, job);
  }

  updateJob(id, updates) {
    return this.update(STORAGE_KEYS.JOBS, id, updates);
  }

  deleteJob(id) {
    this.delete(STORAGE_KEYS.JOBS, id);
  }

  // Candidate methods
  getCandidates() {
    return this.getAll(STORAGE_KEYS.CANDIDATES);
  }

  getCandidate(id) {
    return this.getById(STORAGE_KEYS.CANDIDATES, id);
  }

  createCandidate(candidate) {
    return this.create(STORAGE_KEYS.CANDIDATES, candidate);
  }

  updateCandidate(id, updates) {
    return this.update(STORAGE_KEYS.CANDIDATES, id, updates);
  }

  deleteCandidate(id) {
    this.delete(STORAGE_KEYS.CANDIDATES, id);
  }

  // Placement methods
  getPlacements() {
    return this.getAll(STORAGE_KEYS.PLACEMENTS);
  }

  getPlacement(id) {
    return this.getById(STORAGE_KEYS.PLACEMENTS, id);
  }

  getPlacementsByJob(jobId) {
    const placements = this.getAll(STORAGE_KEYS.PLACEMENTS);
    return placements.filter(placement => placement.jobId === jobId);
  }

  createPlacement(placement) {
    return this.create(STORAGE_KEYS.PLACEMENTS, placement);
  }

  updatePlacement(id, updates) {
    return this.update(STORAGE_KEYS.PLACEMENTS, id, updates);
  }

  deletePlacement(id) {
    this.delete(STORAGE_KEYS.PLACEMENTS, id);
  }

  // Interview methods
  getInterviews() {
    return this.getAll(STORAGE_KEYS.INTERVIEWS);
  }

  getInterview(id) {
    return this.getById(STORAGE_KEYS.INTERVIEWS, id);
  }

  createInterview(interview) {
    return this.create(STORAGE_KEYS.INTERVIEWS, interview);
  }

  updateInterview(id, updates) {
    return this.update(STORAGE_KEYS.INTERVIEWS, id, updates);
  }

  deleteInterview(id) {
    this.delete(STORAGE_KEYS.INTERVIEWS, id);
  }

  // User methods
  getUsers() {
    return this.getAll(STORAGE_KEYS.USERS);
  }

  getCurrentUser() {
    const user = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    return user ? JSON.parse(user) : null;
  }

  setCurrentUser(user) {
    localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
  }

  clearCurrentUser() {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
  }

  // Dashboard stats
  getDashboardStats() {
    const companies = this.getCompanies();
    const jobs = this.getJobs();
    const candidates = this.getCandidates();
    const placements = this.getPlacements();
    const interviews = this.getInterviews();
    
    const openJobs = jobs.filter(job => job.status === 'Open').length;
    const today = new Date().toISOString().split('T')[0];
    const upcomingInterviews = interviews.filter(
      interview => interview.interviewDate >= today
    ).length;
    
    const applicationStatus = {};
    placements.forEach(placement => {
      applicationStatus[placement.status] = (applicationStatus[placement.status] || 0) + 1;
    });
    
    return {
      totalCompanies: companies.length,
      totalJobs: jobs.length,
      openJobs: openJobs,
      totalCandidates: candidates.length,
      totalApplications: placements.length,
      totalInterviews: interviews.length,
      upcomingInterviews: upcomingInterviews,
      applicationStatus: applicationStatus
    };
  }

  // Initialize dummy data - BETTER DATA
  initializeDummyData() {
    console.log('Checking for dummy data initialization...');
    
    // Flag to track if we need to create default users
    let shouldCreateDefaultUsers = false;
    
    // Check if users exist, create default ones if not
    const existingUsers = this.getAll(STORAGE_KEYS.USERS);
    if (existingUsers.length === 0) {
      console.log('No users found, creating default users...');
      shouldCreateDefaultUsers = true;
      
      const defaultUsers = [
        { 
          id: '1', 
          username: 'admin', 
          password: 'admin123', 
          role: 'admin', 
          name: 'Admin User',
          email: 'admin@jskplacement.com'
        },
        { 
          id: '2', 
          username: 'recruiter', 
          password: 'recruiter123', 
          role: 'recruiter', 
          name: 'John Recruiter',
          email: 'john@jskplacement.com'
        }
      ];
      
      // Check if users already exist to avoid duplicates
      defaultUsers.forEach(user => {
        const userExists = existingUsers.some(existingUser => 
          existingUser.username === user.username || existingUser.email === user.email
        );
        if (!userExists) {
          existingUsers.push(user);
        }
      });
      
      this.save(STORAGE_KEYS.USERS, existingUsers);
      console.log('Default users created/updated');
    } else {
      console.log('Users already exist, skipping user creation');
    }

    // Initialize companies only if not present
    if (this.getAll(STORAGE_KEYS.COMPANIES).length === 0) {
      console.log('Creating dummy companies...');
      const companies = [
        { 
          id: '101', 
          name: 'TechCorp Solutions', 
          industry: 'Technology', 
          contactPerson: 'Alice Johnson', 
          email: 'alice@techcorp.com', 
          phone: '+91 9876543210', 
          address: '123 Tech Street, Bangalore, Karnataka 560001',
          status: 'Active',
          website: 'https://techcorp.com',
          description: 'Leading software development and IT solutions company',
          foundedYear: '2015'
        },
        { 
          id: '102', 
          name: 'Global Finance Inc', 
          industry: 'Finance', 
          contactPerson: 'Bob Smith', 
          email: 'bob@globalfinance.com', 
          phone: '+91 9876543211', 
          address: '456 Finance Avenue, Mumbai, Maharashtra 400001',
          status: 'Active',
          website: 'https://globalfinance.com',
          description: 'International financial services and investment banking',
          foundedYear: '2010'
        },
        { 
          id: '103', 
          name: 'MediHealth Group', 
          industry: 'Healthcare', 
          contactPerson: 'Carol Davis', 
          email: 'carol@medihealth.com', 
          phone: '+91 9876543212', 
          address: '789 Health Boulevard, Delhi 110001',
          status: 'Active',
          website: 'https://medihealth.com',
          description: 'Healthcare services and medical equipment provider',
          foundedYear: '2018'
        },
        { 
          id: '104', 
          name: 'Retail Giant', 
          industry: 'Retail', 
          contactPerson: 'David Wilson', 
          email: 'david@retailgiant.com', 
          phone: '+91 9876543213', 
          address: '321 Mall Road, Hyderabad, Telangana 500001',
          status: 'Active',
          website: 'https://retailgiant.com',
          description: 'National retail chain with 500+ stores',
          foundedYear: '2005'
        }
      ];
      this.save(STORAGE_KEYS.COMPANIES, companies);
      console.log('Companies created:', companies.length);
    } else {
      console.log('Companies already exist, skipping creation');
    }

    // Initialize jobs only if not present
    if (this.getAll(STORAGE_KEYS.JOBS).length === 0) {
      console.log('Creating dummy jobs...');
      const jobs = [
        { 
          id: '201', 
          companyId: '101', 
          title: 'Senior React Developer', 
          description: 'Looking for an experienced React developer to lead frontend development projects. Must have strong experience in modern React ecosystem.', 
          requirements: 'React, JavaScript, TypeScript, Redux, HTML5, CSS3, Git', 
          experience: '5+ years', 
          salary: '₹15-20 LPA', 
          status: 'Open', 
          createdAt: '2024-01-15',
          location: 'Bangalore',
          jobType: 'Full-time',
          vacancies: 2
        },
        { 
          id: '202', 
          companyId: '101', 
          title: 'DevOps Engineer', 
          description: 'DevOps engineer responsible for cloud infrastructure, CI/CD pipelines, and automation.', 
          requirements: 'AWS, Docker, Kubernetes, Jenkins, Terraform, Linux', 
          experience: '3+ years', 
          salary: '₹12-16 LPA', 
          status: 'Open', 
          createdAt: '2024-01-20',
          location: 'Remote',
          jobType: 'Full-time',
          vacancies: 1
        },
        { 
          id: '203', 
          companyId: '102', 
          title: 'Financial Analyst', 
          description: 'Financial analyst for investment banking division. Responsible for financial modeling and analysis.', 
          requirements: 'Excel, Financial Modeling, Accounting, SQL, CFA preferred', 
          experience: '2+ years', 
          salary: '₹8-12 LPA', 
          status: 'Open', 
          createdAt: '2024-01-25',
          location: 'Mumbai',
          jobType: 'Full-time',
          vacancies: 3
        },
        { 
          id: '204', 
          companyId: '103', 
          title: 'Healthcare Administrator', 
          description: 'Administrative role for hospital management and operations.', 
          requirements: 'Hospital Management, MS Office, Communication Skills', 
          experience: '4+ years', 
          salary: '₹6-9 LPA', 
          status: 'Open', 
          createdAt: '2024-01-28',
          location: 'Delhi',
          jobType: 'Full-time',
          vacancies: 2
        }
      ];
      this.save(STORAGE_KEYS.JOBS, jobs);
      console.log('Jobs created:', jobs.length);
    } else {
      console.log('Jobs already exist, skipping creation');
    }

    // Initialize candidates only if not present
    if (this.getAll(STORAGE_KEYS.CANDIDATES).length === 0) {
      console.log('Creating dummy candidates...');
      const candidates = [
        { 
          id: '301', 
          name: 'Michael Chen', 
          email: 'michael.chen@email.com', 
          phone: '+91 9876543301', 
          skills: 'React, JavaScript, TypeScript, Node.js, Express, MongoDB', 
          experience: '6 years', 
          currentCompany: 'TechStart Inc', 
          noticePeriod: '30 days',
          expectedSalary: '₹18 LPA',
          location: 'Bangalore',
          status: 'Active',
          resumeUrl: 'https://drive.google.com/michael-chen-resume'
        },
        { 
          id: '302', 
          name: 'Sarah Williams', 
          email: 'sarah.williams@email.com', 
          phone: '+91 9876543302', 
          skills: 'AWS, Docker, Kubernetes, Terraform, Jenkins, Python', 
          experience: '4 years', 
          currentCompany: 'CloudSystems', 
          noticePeriod: '15 days',
          expectedSalary: '₹14 LPA',
          location: 'Hyderabad',
          status: 'Active',
          resumeUrl: 'https://drive.google.com/sarah-williams-resume'
        },
        { 
          id: '303', 
          name: 'David Miller', 
          email: 'david.miller@email.com', 
          phone: '+91 9876543303', 
          skills: 'Financial Analysis, Excel, SQL, Python, Tableau, Accounting', 
          experience: '3 years', 
          currentCompany: 'BankLocal', 
          noticePeriod: '60 days',
          expectedSalary: '₹10 LPA',
          location: 'Mumbai',
          status: 'Active',
          resumeUrl: 'https://drive.google.com/david-miller-resume'
        },
        { 
          id: '304', 
          name: 'Emma Garcia', 
          email: 'emma.garcia@email.com', 
          phone: '+91 9876543304', 
          skills: 'React, TypeScript, Next.js, GraphQL, Jest, Cypress', 
          experience: '3 years', 
          currentCompany: 'WebDev Co', 
          noticePeriod: '45 days',
          expectedSalary: '₹12 LPA',
          location: 'Pune',
          status: 'Active',
          resumeUrl: 'https://drive.google.com/emma-garcia-resume'
        },
        { 
          id: '305', 
          name: 'Rajesh Kumar', 
          email: 'rajesh.kumar@email.com', 
          phone: '+91 9876543305', 
          skills: 'Java, Spring Boot, Microservices, AWS, SQL', 
          experience: '5 years', 
          currentCompany: 'IT Solutions Ltd', 
          noticePeriod: '30 days',
          expectedSalary: '₹16 LPA',
          location: 'Chennai',
          status: 'Active',
          resumeUrl: 'https://drive.google.com/rajesh-kumar-resume'
        }
      ];
      this.save(STORAGE_KEYS.CANDIDATES, candidates);
      console.log('Candidates created:', candidates.length);
    } else {
      console.log('Candidates already exist, skipping creation');
    }

    // Initialize placements only if not present
    if (this.getAll(STORAGE_KEYS.PLACEMENTS).length === 0) {
      console.log('Creating dummy placements...');
      const placements = [
        { 
          id: '401', 
          jobId: '201', 
          candidateId: '301', 
          status: APPLICATION_STATUS.INTERVIEW_IN_PROGRESS, 
          appliedDate: '2024-01-16', 
          notes: 'Technical interview scheduled for Jan 25. Candidate showed strong React knowledge.',
          updatedAt: '2024-01-20'
        },
        { 
          id: '402', 
          jobId: '201', 
          candidateId: '304', 
          status: APPLICATION_STATUS.RESUME_SENT, 
          appliedDate: '2024-01-17', 
          notes: 'Resume sent to hiring manager for review.',
          updatedAt: '2024-01-18'
        },
        { 
          id: '403', 
          jobId: '202', 
          candidateId: '302', 
          status: APPLICATION_STATUS.SELECTED, 
          appliedDate: '2024-01-21', 
          notes: 'Offer letter sent. Expected joining date: Feb 15.',
          updatedAt: '2024-01-25'
        },
        { 
          id: '404', 
          jobId: '203', 
          candidateId: '303', 
          status: APPLICATION_STATUS.JOINED, 
          appliedDate: '2024-01-26', 
          notes: 'Successfully joined on Feb 1, 2024. Onboarding completed.',
          updatedAt: '2024-02-01'
        },
        { 
          id: '405', 
          jobId: '204', 
          candidateId: '305', 
          status: APPLICATION_STATUS.REJECTED, 
          appliedDate: '2024-01-29', 
          notes: 'Candidate did not meet experience requirements.',
          updatedAt: '2024-02-02'
        }
      ];
      this.save(STORAGE_KEYS.PLACEMENTS, placements);
      console.log('Placements created:', placements.length);
    } else {
      console.log('Placements already exist, skipping creation');
    }

    // Initialize interviews only if not present
    if (this.getAll(STORAGE_KEYS.INTERVIEWS).length === 0) {
      console.log('Creating dummy interviews...');
      const interviews = [
        {
          id: '501',
          placementId: '401',
          interviewDate: '2024-01-25',
          interviewTime: '14:30',
          interviewType: 'Technical',
          interviewer: 'John Doe (Tech Lead)',
          status: 'Scheduled',
          notes: 'First round technical interview - Focus on React concepts, problem solving, and system design.',
          location: 'Google Meet: meet.google.com/abc-xyz',
          duration: '60',
          createdAt: '2024-01-20'
        },
        {
          id: '502',
          placementId: '403',
          interviewDate: '2024-01-22',
          interviewTime: '11:00',
          interviewType: 'HR',
          interviewer: 'Jane Smith (HR Manager)',
          status: 'Completed',
          notes: 'Final HR round completed successfully. Salary negotiation in progress.',
          location: 'Office Conference Room A',
          duration: '45',
          createdAt: '2024-01-18'
        },
        {
          id: '503',
          placementId: '402',
          interviewDate: '2024-02-05',
          interviewTime: '10:00',
          interviewType: 'Technical',
          interviewer: 'Robert Johnson (Senior Developer)',
          status: 'Scheduled',
          notes: 'Initial screening interview.',
          location: 'Zoom: zoom.us/j/123456',
          duration: '30',
          createdAt: '2024-01-30'
        }
      ];
      this.save(STORAGE_KEYS.INTERVIEWS, interviews);
      console.log('Interviews created:', interviews.length);
    } else {
      console.log('Interviews already exist, skipping creation');
    }

    console.log('✅ Dummy data initialization check COMPLETE!');
    console.log('Note: Data was only created if it did not already exist.');
  }
}

// Create and export instance
const storageService = new StorageService();
export default storageService;